<template>
  <div>
    <h4 class="text-center mb-3">
      {{ editMode ? 'Modifier une dépense' : 'Ajouter une dépense' }}
    </h4>

    <form @submit.prevent="submitForm" data-cy="form-depense">
      <div class="mb-3">
        <input-component
          id="input-depense-nom"
          label="Nom"
          v-model="formData.nom"
          type="text"
          placeholder="Saisir le nom"
          :class="{ 'is-invalid': errors.nom }"
          @input="clearError('nom')"
          @blur="validateNom"
          required
        />
        <div v-if="errors.nom" class="invalid-feedback d-block">
          {{ errors.nom }}
        </div>
      </div>

      <div class="mb-3">
        <input-component
          id="input-depense-montant"
          label="Montant"
          v-model="formData.montant"
          type="text"
          placeholder="Saisir le montant (ex: 10,50)"
          :class="{ 'is-invalid': errors.montant }"
          @input="clearError('montant')"
          @blur="validateMontant"
          required
        />
        <div v-if="errors.montant" class="invalid-feedback d-block">
          {{ errors.montant }}
        </div>
      </div>

      <div class="mb-3">
        <autocomplete-component
          id="autocomplete-depense"
          label="Catégorie"
          v-model="formData.categorie"
          :items="categoriesList"
          :loading-input="categorieStore.isLoadingForm"
          placeholder="Sélectionnez une catégorie"
          :class="{ 'is-invalid': errors.categorie }"
          @input="clearError('categorie')"
        />
        <div v-if="errors.categorie" class="invalid-feedback d-block">
          {{ errors.categorie }}
        </div>
      </div>

      <div v-if="!editMode" class="mb-3">
        <label class="glass-label">Type de dépense</label>
        <div class="d-flex gap-1 flex-column flex-sm-row">
          <div class="w-100">
            <input
              type="radio"
              class="btn-check"
              name="type-depense"
              id="type-depense-unique"
              :value="false"
              v-model="formData.isRecurrent"
              autocomplete="off"
            />
            <label class="glass-btn-radio px-4 w-100" for="type-depense-unique">
              Dépense ponctuelle
            </label>
          </div>
          <div class="w-100">
            <input
              type="radio"
              class="btn-check"
              name="type-depense"
              id="type-depense-recurrente"
              :value="true"
              v-model="formData.isRecurrent"
              autocomplete="off"
            />
            <label class="glass-btn-radio px-4 w-100" for="type-depense-recurrente">
              Dépense récurrente
            </label>
          </div>
        </div>
      </div>

      <!-- Champs pour dépense unique -->
      <div v-if="!formData.isRecurrent" class="mb-3">
        <dateselection-component
          id="depense"
          label="Date de la dépense"
          v-model="formData.dateDepense"
          :class="{ 'is-invalid': errors.dateDepense }"
          @input="clearError('dateDepense')"
          @change="validateDateDepense"
        />
        <div v-if="errors.dateDepense" class="invalid-feedback d-block">
          {{ errors.dateDepense }}
        </div>
      </div>

      <!-- Champs pour récurrence -->
      <template v-if="formData.isRecurrent">
        <div class="mb-3" data-cy="depense-recurrence-section">
          <label for="inputGroupSelect01">Périodicité</label>

          <div class="d-flex justify-content-between">
            <div class="d-flex flex-column gap-sm-1 w-100">
              <div class="d-flex gap-1">
                <div
                  class="w-100"
                  v-for="option in periodiciteOptions.slice(0, 2)"
                  :key="option.id"
                >
                  <input
                    type="radio"
                    class="btn-check"
                    name="periodicite-options-tablet"
                    :id="'periodicite-option-tablet-' + option.id"
                    :value="option.name"
                    v-model="formData.periodicite"
                    autocomplete="off"
                    required
                  />
                  <label
                    class="glass-btn-radio px-4 w-100"
                    :for="'periodicite-option-tablet-' + option.id"
                  >
                    {{ option.name }}
                  </label>
                </div>
              </div>

              <div class="d-flex gap-1">
                <div
                  class="w-100"
                  v-for="option in periodiciteOptions.slice(2, 4)"
                  :key="option.id"
                >
                  <input
                    type="radio"
                    class="btn-check"
                    name="periodicite-options-tablet"
                    :id="'periodicite-option-tablet-' + option.id"
                    :value="option.name"
                    v-model="formData.periodicite"
                    autocomplete="off"
                    required
                  />
                  <label
                    class="glass-btn-radio px-4 w-100"
                    :for="'periodicite-option-tablet-' + option.id"
                  >
                    {{ option.name }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-3">
          <template v-if="formData.periodicite === 'Hebdomadaire'">
            <select-component
              id="select-depense-jour-semaine"
              label="Jour de la récurrence"
              v-model="formData.jour_semaine"
              :options="joursSemaine"
              :class="{ 'is-invalid': errors.jour_semaine }"
              @change="(clearError('jour_semaine'), validatePeriodiciteFields())"
              required
            />
            <div v-if="errors.jour_semaine" class="invalid-feedback d-block">
              {{ errors.jour_semaine }}
            </div>
          </template>
          <template v-else-if="formData.periodicite === 'Mensuelle'">
            <dateselection-component
              id="depense-recurrence-mois"
              label="Date de la récurrence"
              v-model="formData.jour_mois"
              :class="{ 'is-invalid': errors.jour_mois }"
              @input="clearError('jour_mois')"
              @change="validatePeriodiciteFields"
              no-year-picker
              no-month-picker
            />
            <div v-if="errors.jour_mois" class="invalid-feedback d-block">
              {{ errors.jour_mois }}
            </div>
          </template>
          <template v-else-if="formData.periodicite === 'Annuelle'">
            <dateselection-component
              id="depense-recurrence-annee"
              label="Date et mois de la récurrence"
              v-model="formData.jour_annee"
              :class="{ 'is-invalid': errors.jour_annee }"
              @input="clearError('jour_annee')"
              @change="validatePeriodiciteFields"
              no-year-picker
            />
            <div v-if="errors.jour_annee" class="invalid-feedback d-block">
              {{ errors.jour_annee }}
            </div>
          </template>
        </div>
      </template>

      <div class="form-actions">
        <button
          type="button"
          class="glass-btn-danger-outlined rounded-5 py-2 px-3"
          data-cy="form-depense-cancel"
          @click="$emit('close')"
        >
          Annuler
        </button>
        <button
          type="submit"
          class="glass-btn-success rounded-5 py-2 px-3"
          data-cy="form-depense-submit"
          :disabled="isSubmitting"
        >
          {{ editMode ? 'Modifier' : 'Ajouter' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
// Component
import InputComponent from '@/components/InputComponent.vue'
import AutocompleteComponent from '@/components/AutocompleteComponent.vue'
import DateselectionComponent from '@/components/DateselectionComponent.vue'
import SelectComponent from '@/components/SelectComponent.vue'

// Store
import useCategorieStore from '@/stores/categorie'
import useCategorieDepenseStore from '@/stores/categorieDepense'
import useDepenseStore from '@/stores/depense'
import useRecurrenceStore from '@/stores/recurrence'

export default {
  name: 'DepenseFormBlock',

  components: {
    InputComponent,
    AutocompleteComponent,
    DateselectionComponent,
    SelectComponent
  },

  props: {
    editMode: {
      type: Boolean,
      default: false
    },
    itemToEdit: {
      type: Object,
      default: null
    },
    preselectedCategorieId: {
      type: Number,
      default: null
    }
  },

  setup() {
    const categorieStore = useCategorieStore()
    const { addDepense } = useCategorieDepenseStore()
    const { updateDepense } = useDepenseStore()
    const { addRecurrence, updateRecurrence, handleName, handleNameReverse } = useRecurrenceStore()
    return {
      categorieStore,
      addDepense,
      updateDepense,
      addRecurrence,
      updateRecurrence,
      handleName,
      handleNameReverse
    }
  },

  emits: ['close'],

  data() {
    return {
      formData: {
        nom: null,
        montant: null,
        categorie: null,
        dateDepense: null,
        isRecurrent: false,
        periodicite: 'Mensuelle',
        jour_semaine: null,
        jour_mois: null,
        jour_annee: null
      },
      errors: {
        nom: null,
        montant: null,
        categorie: null,
        dateDepense: null,
        periodicite: null,
        jour_semaine: null,
        jour_mois: null,
        jour_annee: null
      },
      isSubmitting: false,
      periodiciteOptions: [
        { id: 1, name: 'Journalière', value: 'journaliere' },
        { id: 2, name: 'Hebdomadaire', value: 'hebdomadaire' },
        { id: 3, name: 'Mensuelle', value: 'mensuelle' },
        { id: 4, name: 'Annuelle', value: 'annuelle' }
      ],
      joursSemaine: [
        { id: 1, value: 'Lundi' },
        { id: 2, value: 'Mardi' },
        { id: 3, value: 'Mercredi' },
        { id: 4, value: 'Jeudi' },
        { id: 5, value: 'Vendredi' },
        { id: 6, value: 'Samedi' },
        { id: 7, value: 'Dimanche' }
      ]
    }
  },

  computed: {
    categoriesList() {
      if (this.categorieStore.isLoadingForm) return []
      return this.categorieStore.categories
    }
  },

  mounted() {
    this.categorieStore.loadCategories(true).then(() => {
      if (this.editMode && this.itemToEdit) {
        this.formData.categorie = this.categorieStore.getCategorieById(this.itemToEdit.idCategorie)
      } else if (this.preselectedCategorieId) {
        // Pré-sélectionner la catégorie si elle est fournie
        this.formData.categorie = this.categorieStore.getCategorieById(this.preselectedCategorieId)
      }
    })

    if (this.editMode && this.itemToEdit) {
      this.formData = {
        nom: this.itemToEdit.libelle,
        montant: this.itemToEdit.montant.toString(),
        categorie: this.formData.categorie,
        lastIdCategorie: this.itemToEdit.idCategorie,
        dateDepense: this.$dayjs.unix(this.itemToEdit.date).toDate(),
        isRecurrent: false,
        periodicite: 'Mensuelle',
        jour_semaine: null,
        jour_mois: null,
        jour_annee: null
      }
    }

    if (!this.editMode) {
      // Préremplir la date de dépense avec la date du jour pour une nouvelle dépense
      this.formData.dateDepense = this.$dayjs().toDate()
    }

    // Si je suis sur la page des récurrences, j'active directement le mode récurrent
    if (!this.editMode && this.$route.name === 'recurrences') {
      this.formData.isRecurrent = true
    }
  },

  methods: {
    validateNom() {
      const nom = this.formData.nom
      if (!nom || nom.trim() === '') {
        this.errors.nom = 'Le nom est obligatoire'
        return false
      }

      // Regex pour valider le nom : lettres, chiffres, espaces, tirets, caractères UTF-8 uniquement
      const validNameRegex = /^[\p{L}\p{M}\p{N}\s-]+$/u
      if (!validNameRegex.test(nom)) {
        this.errors.nom = 'Le nom ne doit contenir que des lettres, chiffres, espaces et tirets'
        return false
      }

      // Le nom doit avoir au maximum 100 caractères
      if (nom.length > 100) {
        this.errors.nom = 'Le nom ne doit pas dépasser 100 caractères'
        return false
      }

      this.errors.nom = null
      return true
    },

    validateMontant() {
      const montant = this.formData.montant
      if (!montant) {
        this.errors.montant = 'Le montant est obligatoire'
        return false
      }

      // Convertir en string pour validation
      const montantStr = montant.toString()

      // Regex pour valider un montant en euros avec max 2 décimales
      const validAmountRegex = /^\d+([.,]\d{1,2})?$/
      if (!validAmountRegex.test(montantStr)) {
        this.errors.montant = 'Le montant doit être un nombre avec maximum 2 décimales (ex: 10,50)'
        return false
      }

      const numericValue = parseFloat(montantStr.replace(',', '.'))
      if (numericValue <= 0) {
        this.errors.montant = 'Le montant doit être supérieur à 0'
        return false
      }

      this.errors.montant = null
      return true
    },

    validateCategorie() {
      if (!this.formData.categorie || !this.formData.categorie.id) {
        this.errors.categorie = 'La catégorie est obligatoire'
        return false
      }

      this.errors.categorie = null
      return true
    },

    validateDateDepense() {
      if (!this.formData.isRecurrent && !this.formData.dateDepense) {
        this.errors.dateDepense = 'La date de dépense est obligatoire'
        return false
      }

      this.errors.dateDepense = null
      return true
    },

    validatePeriodiciteFields() {
      if (!this.formData.isRecurrent) return true

      const periodicite = this.formData.periodicite
      let isValid = true

      // Réinitialiser les erreurs des champs conditionnels
      this.errors.jour_semaine = null
      this.errors.jour_mois = null
      this.errors.jour_annee = null

      switch (periodicite) {
        case 'Hebdomadaire':
          if (!this.formData.jour_semaine) {
            this.errors.jour_semaine =
              'Le jour de la semaine est obligatoire pour une récurrence hebdomadaire'
            isValid = false
          }
          break

        case 'Mensuelle':
          if (!this.formData.jour_mois) {
            this.errors.jour_mois = 'Le jour du mois est obligatoire pour une récurrence mensuelle'
            isValid = false
          }
          break

        case 'Annuelle':
          if (!this.formData.jour_annee) {
            this.errors.jour_annee = 'La date est obligatoire pour une récurrence annuelle'
            isValid = false
          }
          break
      }

      return isValid
    },

    validateForm() {
      const isNomValid = this.validateNom()
      const isMontantValid = this.validateMontant()
      const isCategorieValid = this.validateCategorie()

      if (this.formData.isRecurrent) {
        const isPeriodiciteValid = this.validatePeriodiciteFields()
        return isNomValid && isMontantValid && isCategorieValid && isPeriodiciteValid
      } else {
        const isDateValid = this.validateDateDepense()
        return isNomValid && isMontantValid && isCategorieValid && isDateValid
      }
    },

    clearError(field) {
      this.errors[field] = null
    },

    async submitForm() {
      if (this.isSubmitting) return

      // Valider le formulaire avant soumission
      if (!this.validateForm()) {
        this.$toast.error('Veuillez corriger les erreurs dans le formulaire')
        return
      }

      this.isSubmitting = true

      try {
        if (this.formData.isRecurrent) {
          // Traitement pour une récurrence
          const formattedFormInfo = {
            libelle: this.formData.nom.trim(),
            idCategorie: this.formData.categorie.id,
            montant: parseFloat(this.formData.montant.toString().replace(',', '.')),
            periodicite: this.handleNameReverse(this.formData.periodicite),
            jourSemaine: this.formData.jour_semaine ?? null,
            jourMois: this.formData.jour_mois
              ? parseInt(this.$dayjs(this.formData.jour_mois).format('DD'))
              : null,
            jourAnnee: this.formData.jour_annee
              ? this.$dayjs(this.formData.jour_annee).format('DD-MM')
              : null,
            actif: true
          }

          await this.$toast.promise(this.addRecurrence(formattedFormInfo), {
            pending: 'Ajout en cours...',
            success: {
              render: 'Récurrence ajoutée !',
              autoClose: 1500
            },
            error: {
              render: "Erreur lors de l'ajout de la récurrence.",
              autoClose: 1500
            }
          })
        } else {
          // Traitement pour une dépense unique
          const formattedFormInfo = {
            libelle: this.formData.nom.trim(),
            idCategorie: this.formData.categorie.id,
            date: this.$dayjs(this.formData.dateDepense).unix(),
            montant: parseFloat(this.formData.montant.toString().replace(',', '.')),
            lastIdCategorie: this.formData.lastIdCategorie ?? this.formData.categorie.id,
            lastDate: this.editMode
              ? this.$dayjs(this.itemToEdit.date * 1000).unix()
              : this.formData.dateDepense
          }

          if (this.editMode) {
            formattedFormInfo.id = this.itemToEdit.id
            formattedFormInfo.lastMontant = parseFloat(
              this.itemToEdit.montant.toString().replace(',', '.')
            )

            await this.$toast.promise(this.updateDepense(formattedFormInfo), {
              pending: 'Mise à jour en cours...',
              success: {
                render: 'Dépense mise à jour !',
                autoClose: 1500
              },
              error: {
                render: 'Erreur lors de la mise à jour de la dépense.',
                autoClose: 1500
              }
            })
          } else {
            await this.$toast.promise(this.addDepense(formattedFormInfo), {
              pending: 'Ajout en cours...',
              success: {
                render: 'Dépense ajoutée !',
                autoClose: 1500
              },
              error: {
                render: "Erreur lors de l'ajout de la dépense.",
                autoClose: 1500
              }
            })
          }
        }

        this.$emit('close')
      } catch (error) {
        console.error("Erreur lors de l'opération:", error)
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>
